/**
 * Collapsible card showing a tool call and its result.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench, CheckCircle, AlertCircle } from 'lucide-react';

const TOOL_LABELS = {
  get_health_score: 'Health Score',
  get_margin_guard: 'Margin Guard',
  get_deals: 'Deals',
  get_products: 'Products',
  get_inventory: 'Inventory',
  get_agent_activity: 'Agent Activity',
  get_goals: 'Goals',
  get_guardrails: 'Guardrails',
  get_recommendations: 'Recommendations',
  get_subscription: 'Subscription',
  get_orders: 'Orders',
  get_pricing_intelligence: 'Pricing Intelligence',
  get_seo_audit: 'SEO Audit',
  get_feedback_summary: 'Feedback',
  get_demand_signals: 'Demand Signals',
  get_geographic_distribution: 'Geographic',
  get_channel_performance: 'Channels',
  get_decision_traces: 'Decisions',
  get_ci_score_v2: 'CI Score v2',
  get_city_index: 'City Index',
  get_category_index: 'Category Index',
  get_report: 'Report',
  create_deal_proposal: 'Create Deal',
  create_price_change_proposal: 'Price Change',
  create_goal: 'Create Goal',
  resolve_fix: 'Resolve Fix',
  snooze_fix: 'Snooze Fix',
  toggle_agent: 'Toggle Agent',
  update_guardrails: 'Update Guardrails',
  trigger_shopify_sync: 'Shopify Sync',
  approve_proposal: 'Approve Proposal',
};

export default function ChatToolCard({ toolCall }) {
  const [expanded, setExpanded] = useState(false);
  const { name, input, result } = toolCall;
  const label = TOOL_LABELS[name] || name;
  const held = result?.status==='held_for_first_release' || result?.status==='unavailable';
  const hasError = result?.error;
  const isAction = name.startsWith('create_') || name.startsWith('resolve_') || name.startsWith('snooze_') || name.startsWith('toggle_') || name.startsWith('update_') || name.startsWith('trigger_') || name.startsWith('approve_');

  return (
    <div className={`my-2 rounded-lg border ${hasError ? 'border-red-500/30 bg-red-500/5' : 'border-white/10 bg-white/5'}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-white/5 rounded-lg transition-colors"
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Wrench className="w-3 h-3 text-[#3EF1C7]" />
        <span className="font-medium">{label}</span>
        {held && <span className="text-xs text-amber-400">Unavailable</span>}
        {isAction && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">action</span>}
        <span className="flex-1" />
        {result ? (
          held || hasError ? <AlertCircle className="w-3 h-3 text-red-400" /> : <CheckCircle className="w-3 h-3 text-green-400" />
        ) : (
          <span className="text-xs text-white/40 animate-pulse">running...</span>
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {Object.keys(input || {}).length > 0 && (
            <div>
              <span className="text-xs text-white/40 uppercase">Input</span>
              <pre className="text-xs bg-black/30 rounded p-2 overflow-x-auto mt-1">
                {JSON.stringify(input, null, 2)}
              </pre>
            </div>
          )}
          {result && (
            <div>
              <span className="text-xs text-white/40 uppercase">Result</span>
              <pre className="text-xs bg-black/30 rounded p-2 overflow-x-auto mt-1 max-h-48 overflow-y-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
